import { USER_WORKER_URL, isSfwSite } from '../config.js';
import { getCurrentLanguage, getByPath, interpolate } from '../translations.js';
import { formatLocalizedNumber } from './localeFormat.js';
import { storage } from './storage.js';
import { tokenManager } from './TokenManager.js';

const UI_PLAN_TO_DISPLAY_NAME = {
  starter: 'Starter',
  single: 'Premium',
  harem: 'Creator',
  premium: 'Premium',
  creator: 'Creator',
  trial: 'Trial Offer',
};

export const TIER_UPGRADE_LADDER = Object.freeze([
  { membershipTier: 'trial_offer', nextUiPlan: 'starter' },
  { membershipTier: 'starter', nextUiPlan: 'single' },
  { membershipTier: 'single', nextUiPlan: 'harem' },
]);

const UI_PLAN_TO_TRANSLATION_KEY = {
  starter: 'pricing.starter.title',
  single: 'pricing.premium.title',
  harem: 'pricing.creator.title',
  trial: 'pricing.trial.title',
};

export const isTrialOfferEligible = () => {
  if (!isSfwSite()) return false;
  const userData = storage.get('userData');
  const isLoggedIn = tokenManager.isAuthenticated() && userData?.isLoggedIn === true;
  if (!isLoggedIn) return true;

  const currentTier = String(userData?.current_tier || 'free').trim().toLowerCase();
  if (currentTier !== 'free') return false;
  return userData?.boughtTrial !== true;
};

const normalizeDisplayName = (value) => String(value || '').trim().toLowerCase();

export const resolveNextUpgradeUiPlan = (currentTier) => {
  const normalized = String(currentTier || 'free').trim().toLowerCase();
  const entry = TIER_UPGRADE_LADDER.find((item) => item.membershipTier === normalized);
  return entry?.nextUiPlan ?? null;
};

export const formatTierDisplayName = (uiPlanKey, lang = getCurrentLanguage()) => {
  const translationKey = UI_PLAN_TO_TRANSLATION_KEY[uiPlanKey];
  if (!translationKey) {
    return UI_PLAN_TO_DISPLAY_NAME[uiPlanKey] || String(uiPlanKey || '');
  }

  const pricingRoot = getPricingTranslations(lang);
  return getByPath(pricingRoot, translationKey)
    ?? getByPath(window.translations?.en?.pricing, translationKey)
    ?? UI_PLAN_TO_DISPLAY_NAME[uiPlanKey]
    ?? uiPlanKey;
};

let cachedPlans = null;
let fetchPromise = null;

export const formatPlanNumber = (value, lang = getCurrentLanguage()) =>
  formatLocalizedNumber(value, lang);

export const formatPlanPrice = (priceUsd, lang = getCurrentLanguage()) => {
  const n = Number(priceUsd);
  if (!Number.isFinite(n)) return '$0';

  if (lang === 'fr') {
    const formatted = Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
    return `${formatted} $`;
  }

  const formatted = Number.isInteger(n) ? String(n) : n.toFixed(2);
  return `$${formatted}`;
};

export const fetchMembershipPlans = async ({ force = false } = {}) => {
  if (!force && cachedPlans) return cachedPlans;
  if (!force && fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    const response = await fetch(`${USER_WORKER_URL}/membership-plans`);
    if (!response.ok) throw new Error(`Failed to fetch membership plans (${response.status})`);

    const payload = await response.json();
    const plans = payload?.data?.plans;
    cachedPlans = Array.isArray(plans) ? plans : [];
    return cachedPlans;
  })();

  try {
    return await fetchPromise;
  } finally {
    fetchPromise = null;
  }
};

export const getPlanByTier = (uiPlanKey, plans = cachedPlans) => {
  if (!Array.isArray(plans)) return null;

  const displayName = UI_PLAN_TO_DISPLAY_NAME[uiPlanKey] || uiPlanKey;
  const normalizedTarget = normalizeDisplayName(displayName);

  return plans.find(
    (plan) => normalizeDisplayName(plan.display_name) === normalizedTarget
  ) || null;
};

const getPricingTranslations = (lang) =>
  window.translations?.[lang]?.pricing
  ?? window.translations?.en?.pricing
  ?? {};

const resolveFeatureTemplate = (featureKey, lang) => {
  const pricingRoot = getPricingTranslations(lang);
  const key = featureKey.startsWith('pricing.') ? featureKey : `pricing.${featureKey}`;
  return getByPath(pricingRoot, key) ?? getByPath(window.translations?.en?.pricing, key) ?? '';
};

const getFeatureVars = (featureKey, plan, lang, plans) => {
  const starterPlan = getPlanByTier('starter', plans);
  const trialPlan = getPlanByTier('trial', plans);

  const vars = {
    messages: formatPlanNumber(plan?.estimated_messages ?? 0, lang),
    audio_messages: formatPlanNumber(plan?.estimated_audio_messages ?? 0, lang),
    tokens: formatPlanNumber(plan?.max_context_tokens ?? 0, lang),
    count: '0',
    price: formatPlanPrice(plan?.monthly_price_usd ?? 0, lang),
    trial_price: formatPlanPrice(trialPlan?.monthly_price_usd ?? 0, lang),
    starter_price: formatPlanPrice(starterPlan?.monthly_price_usd ?? 0, lang),
    memory_suffix: '',
  };

  if (featureKey.endsWith('.features.4')) {
    vars.count = formatPlanNumber(plan?.character_creation ?? 0, lang);
  }
  if (featureKey.endsWith('.features.5')) {
    vars.count = formatPlanNumber(plan?.scenario_creation ?? 0, lang);
  }
  if (featureKey.endsWith('.features.6')) {
    vars.count = formatPlanNumber(plan?.sprite_credits ?? 0, lang);
  }
  if (featureKey.includes('quota_exhausted.button')
    || featureKey.includes('trial_modal.button')
    || featureKey.includes('low_messages.button')) {
    vars.price = formatPlanPrice(trialPlan?.monthly_price_usd ?? plan?.monthly_price_usd ?? 0, lang);
  }

  return vars;
};

const hydratePlanPrices = (container, plans, lang) => {
  container.querySelectorAll('[data-pricing-plan]').forEach((planEl) => {
    const uiPlan = planEl.getAttribute('data-pricing-plan');
    const plan = getPlanByTier(uiPlan, plans);
    if (!plan) return;

    const monthlyEl = planEl.querySelector('.price-amount');
    const yearlyEl = planEl.querySelector('.yearly-price');

    if (monthlyEl) {
      const periodEl = monthlyEl.querySelector('[data-translate]');
      const periodHtml = periodEl ? periodEl.outerHTML : '';
      monthlyEl.innerHTML = `${formatPlanPrice(plan.monthly_price_usd, lang)} ${periodHtml}`.trim();
    }

    if (yearlyEl) {
      const periodEl = yearlyEl.querySelector('[data-translate]');
      const periodHtml = periodEl ? periodEl.outerHTML : '';
      yearlyEl.innerHTML = `${formatPlanPrice(plan.yearly_price_usd, lang)} ${periodHtml}`.trim();
    }
  });

  container.querySelectorAll('[data-pricing-price]').forEach((el) => {
    const priceKind = el.getAttribute('data-pricing-price');
    const plan = getPlanByTier(priceKind === 'trial' ? 'trial' : priceKind, plans);
    if (!plan) return;
    el.textContent = formatPlanPrice(plan.monthly_price_usd, lang);
  });
};

const hydrateFeatureRows = (container, plans, lang) => {
  container.querySelectorAll('[data-pricing-feature]').forEach((el) => {
    const featureKey = el.getAttribute('data-pricing-feature');
    if (!featureKey) return;

    const planTierAttr = el.getAttribute('data-pricing-plan-tier')
      || el.closest('[data-pricing-plan]')?.getAttribute('data-pricing-plan');
    let plan = getPlanByTier(planTierAttr, plans);

    if (!plan && featureKey.includes('quota_exhausted')) {
      plan = featureKey.includes('trial') || featureKey.includes('subtitle_post')
        ? getPlanByTier('trial', plans)
        : getPlanByTier('starter', plans);
    }

    if (!plan && (featureKey.includes('trial_modal') || featureKey.includes('low_messages'))) {
      plan = getPlanByTier('trial', plans);
    }

    if (!plan && featureKey.includes('trial_auth_modal')) {
      plan = getPlanByTier('trial', plans);
    }

    if (!plan) return;

    const template = resolveFeatureTemplate(featureKey, lang);
    if (!template) return;

    const vars = getFeatureVars(featureKey, plan, lang, plans);
    const interpolated = interpolate(template, vars);
    if (interpolated.includes('<')) {
      el.innerHTML = interpolated;
    } else {
      el.textContent = interpolated;
    }

    if (featureKey.endsWith('.features.7')) {
      const isShortContextTier = featureKey.includes('.trial.') || featureKey.includes('.starter.');
      el.classList.remove('yes', 'no');
      el.classList.add(isShortContextTier ? 'no' : 'yes');
    }
  });
};

export const hydratePricingFromPlans = async (container, lang = getCurrentLanguage()) => {
  if (!container) return;

  try {
    const plans = await fetchMembershipPlans();
    if (!plans.length) return;

    hydratePlanPrices(container, plans, lang);
    hydrateFeatureRows(container, plans, lang);
  } catch (error) {
    console.error('[pricingPlans] Failed to hydrate pricing from plans:', error);
  }
};

export const buildInterpolatedFeatures = (uiTier, lang = getCurrentLanguage(), plans = cachedPlans) => {
  const plan = getPlanByTier(uiTier, plans);
  if (!plan || !Array.isArray(plans)) return {};

  const tierKey = uiTier === 'single' || uiTier === 'premium'
    ? 'premium'
    : uiTier === 'harem' || uiTier === 'creator'
      ? 'creator'
      : uiTier === 'trial'
        ? 'trial'
        : 'starter';

  const featureIndexes = [1, 3, 4, 5, 6, 7, 8];
  const result = {};

  featureIndexes.forEach((index) => {
    const featureKey = `pricing.${tierKey}.features.${index}`;
    const template = resolveFeatureTemplate(featureKey, lang);
    if (!template) return;
    result[featureKey] = interpolate(template, getFeatureVars(featureKey, plan, lang, plans));
  });

  return result;
};
